import { useState } from "react";
import useMutateData from "@/hooks/useMutateData";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import DatePicker from "@/components/DatePicker.jsx";

export default function CandidateWorkHistory({ candidateId, workHistory = [], updateCandidateData }) {
    const [entries, setEntries] = useState(workHistory);
    const [editingIndex, setEditingIndex] = useState(null);
    const [newEntry, setNewEntry] = useState(false);
    const { mutate } = useMutateData();

    const handleEdit = (index) => setEditingIndex(index);

    const handleDelete = async (workHistoryId) => {
        if (!window.confirm("Are you sure you want to delete this work history entry?")) return;
        const result = await mutate("DELETE", `admin/work_histories/${workHistoryId}`);
        if (result) {
            const updatedEntries = entries.filter(entry => entry.slugId !== workHistoryId);
            setEntries(updatedEntries);
            updateCandidateData(prev => ({
                ...prev,
                candidate: { ...prev.candidate, workHistories: updatedEntries }
            }));
        }
    };

    const handleSave = async (index, updatedData) => {
        const { slugId, ...filteredData } = updatedData;
        const result = slugId
            ? await mutate("PATCH", `admin/work_histories/${slugId}`, { work_history: filteredData })
            : await mutate("POST", `admin/work_histories`, { work_history: filteredData, candidate_id: candidateId });

        if (result?.work_history) {
            const updated = [...entries];
            updated[index] = result.work_history;
            setEntries(updated);
            updateCandidateData(prev => ({
                ...prev,
                candidate: { ...prev.candidate, workHistories: updated }
            }));
        }

        setEditingIndex(null);
        setNewEntry(false);
    };

    const handleCancel = () => {
        setEditingIndex(null);
        setNewEntry(false);
    };

    const handleAdd = () => {
        setEntries([...entries, { companyName: "", position: "", startDate: "", endDate: "" }]);
        setEditingIndex(entries.length);
        setNewEntry(true);
    };

    return (
        <div className="p-4 border rounded-lg bg-white shadow-sm">
            <h3 className="text-xl font-bold mb-4">Work History</h3>
            <div className="space-y-4">
                {entries.map((job, index) => (
                    <div key={index} className="p-4 border rounded-lg bg-gray-50 shadow-sm">
                        {editingIndex === index ? (
                            <WorkHistoryForm data={job} onSave={(data) => handleSave(index, data)} onCancel={handleCancel} />
                        ) : (
                            <>
                                <h4 className="text-lg font-semibold">{job.companyName}</h4>
                                <p className="text-gray-700"><strong>Position:</strong> {job.position}</p>
                                <p className="text-gray-700"><strong>Start Date:</strong> {job.startDate}</p>
                                <p className="text-gray-700"><strong>End Date:</strong> {job.endDate || "Present"}</p>
                                <div className="mt-3 flex space-x-2">
                                    <Button variant="outline" onClick={() => handleEdit(index)}>Edit</Button>
                                    <Button variant="destructive" onClick={() => handleDelete(job.slugId)}>Delete</Button>
                                </div>
                            </>
                        )}
                    </div>
                ))}

                {!newEntry && (
                    <div
                        className="p-4 border-2 border-dashed border-gray-400 rounded-lg bg-gray-50 flex items-center justify-center cursor-pointer hover:bg-gray-100"
                        onClick={handleAdd}
                    >
                        <span className="text-gray-700 font-semibold">+ Add Work History</span>
                    </div>
                )}
            </div>
        </div>
    );
}

// -------------------- FORM COMPONENT --------------------

function WorkHistoryForm({ data, onSave, onCancel }) {
    const [formData, setFormData] = useState(data);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(formData);
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div>
                <label className="font-medium block mb-1">Company Name</label>
                <Input type="text" name="companyName" value={formData.companyName} onChange={handleChange} required />
            </div>
            <div>
                <label className="font-medium block mb-1">Position</label>
                <Input type="text" name="position" value={formData.position} onChange={handleChange} required />
            </div>
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="font-medium block mb-1">Start Date</label>
                    <DatePicker value={formData.startDate} onChange={(date) => setFormData({ ...formData, startDate: date })} />
                </div>
                <div>
                    <label className="font-medium block mb-1">End Date</label>
                    <DatePicker value={formData.endDate} onChange={(date) => setFormData({ ...formData, endDate: date })} />
                </div>
            </div>
            <div className="flex space-x-2 mt-3">
                <Button type="submit">Save</Button>
                <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
            </div>
        </form>
    );
}

// -------------------- FIXED DATE PICKER --------------------
